/** Where the visitor is, worked out in the browser and nowhere else.
 *
 *  The time zone (Asia/Dubai, Europe/Berlin) names a place well enough to pick a
 *  cloud region; the language region (en-GB) is the fallback. No permission is
 *  asked, no address is looked up and nothing is sent: a VPN or a traveller can
 *  be wrong, which is why the page always offers another country. */
import { useCallback, useEffect, useState } from 'react'
import { track } from './track'

const ZONE_COUNTRY: Record<string, string> = {
  'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Qatar': 'QA', 'Asia/Kuwait': 'KW', 'Asia/Bahrain': 'BH',
  'Asia/Jerusalem': 'IL', 'Asia/Tel_Aviv': 'IL', 'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
  'Asia/Singapore': 'SG', 'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR', 'Asia/Hong_Kong': 'HK', 'Asia/Taipei': 'TW',
  'Asia/Bangkok': 'TH', 'Asia/Jakarta': 'ID', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Manila': 'PH',
  'Asia/Ho_Chi_Minh': 'VN', 'Asia/Saigon': 'VN', 'Pacific/Auckland': 'NZ', 'Pacific/Honolulu': 'US',
  'Europe/London': 'GB', 'Europe/Dublin': 'IE', 'Europe/Paris': 'FR', 'Europe/Berlin': 'DE', 'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT', 'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Zurich': 'CH',
  'Europe/Vienna': 'AT', 'Europe/Stockholm': 'SE', 'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK',
  'Europe/Helsinki': 'FI', 'Europe/Warsaw': 'PL', 'Europe/Athens': 'GR', 'Europe/Istanbul': 'TR',
  'Africa/Johannesburg': 'ZA', 'Africa/Casablanca': 'MA', 'America/Mexico_City': 'MX', 'America/Sao_Paulo': 'BR',
  'America/Santiago': 'CL',
}
const ZONE_PREFIX: [string, string][] = [
  ['Australia/', 'AU'], ['America/Toronto', 'CA'], ['America/Vancouver', 'CA'], ['America/Montreal', 'CA'],
  ['America/Edmonton', 'CA'], ['America/Winnipeg', 'CA'], ['America/Halifax', 'CA'], ['America/Regina', 'CA'],
  ['America/New_York', 'US'], ['America/Chicago', 'US'], ['America/Denver', 'US'], ['America/Los_Angeles', 'US'],
  ['America/Phoenix', 'US'], ['America/Anchorage', 'US'], ['America/Detroit', 'US'], ['America/Indiana', 'US'],
  ['America/Boise', 'US'], ['US/', 'US'],
]

/** Two-letter country of this browser, or null when it cannot tell. */
export function detectCountry(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (zone) {
      if (ZONE_COUNTRY[zone]) return ZONE_COUNTRY[zone]
      const hit = ZONE_PREFIX.find(([p]) => zone.startsWith(p))
      if (hit) return hit[1]
    }
  } catch { /* no Intl: fall through */ }
  try {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const region = /^[a-z]{2,3}[-_]([A-Za-z]{2})\b/.exec(tag)?.[1]
      if (region) return region.toUpperCase()
    }
  } catch { /* no navigator */ }
  return null
}

export function countryName(code: string): string {
  try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code } catch { return code }
}

const KEY = 'country'
type Stored = { code: string; chosen: boolean }
const listeners = new Set<() => void>()
let memory: Stored | null = null     // when storage is blocked the choice lasts for this page

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) { const v = JSON.parse(raw) as Stored; if (v && typeof v.code === 'string') return v }
  } catch { /* blocked or garbage */ }
  return memory
}

/** The country the pages are priced for: the one the visitor chose, else the one detected.
 *  Shared by the Calculator, Compare and Fit through the browser's storage. */
export function useCountry() {
  const [stored, setStored] = useState<Stored | null>(read)
  // `phase` drives the live signal: looking, then found (or nothing to find)
  const [phase, setPhase] = useState<'detecting' | 'done'>(() => (read() ? 'done' : 'detecting'))
  useEffect(() => {
    const on = () => setStored(read())
    listeners.add(on)
    window.addEventListener('storage', on)
    return () => { listeners.delete(on); window.removeEventListener('storage', on) }
  }, [])
  // First visit: look once, with a beat so the visitor sees it happen. A saved
  // country (chosen or detected) is not looked for again.
  useEffect(() => {
    if (read()) { setPhase('done'); return }
    const t = window.setTimeout(() => {
      const code = detectCountry()
      if (code) save({ code, chosen: false })
      setPhase('done')
    }, 1100)
    return () => window.clearTimeout(t)
  }, [])
  const choose = useCallback((code: string) => { track('country', code, '', { code }); save({ code, chosen: true }) }, [])
  const detectAgain = useCallback(() => {
    setPhase('detecting')
    window.setTimeout(() => {
      const code = detectCountry()
      if (code) save({ code, chosen: false })
      setPhase('done')
    }, 900)
  }, [])
  return { code: stored?.code ?? null, chosen: stored?.chosen ?? false, phase, choose, detectAgain }
}

function save(v: Stored) {
  memory = v
  try { localStorage.setItem(KEY, JSON.stringify(v)) } catch { /* blocked: kept in memory */ }
  listeners.forEach((f) => f())
}
